import { useCallback } from "react";
import { useRestQuery } from "../../../hooks/useRestQuery";
import { useRestMutation } from "../../../hooks/useRestMutation";
import * as api from "../services/notify.api";
import type { NotificationsResponse } from "../types";

const NOTIFICATION_POLL_INTERVAL_MS = 30_000;

export function useNotifications(options?: { skip?: boolean }) {
  const {
    data,
    loading,
    error,
    refetch,
    updateCache,
  } = useRestQuery<NotificationsResponse>(
    () => api.getNotifications(),
    {
      key: "notify-notifications",
      refreshInterval: NOTIFICATION_POLL_INTERVAL_MS,
      skip: options?.skip,
    },
  );

  const [triggerMarkAllRead] = useRestMutation(
    useCallback(() => api.markAllRead(), []),
  );

  const [triggerMarkOneRead] = useRestMutation(
    useCallback((id: number) => api.markOneRead(id), []),
  );

  const [triggerDelete] = useRestMutation(
    useCallback((id: number) => api.deleteNotification(id), []),
  );

  const markAllRead = useCallback(async () => {
    await triggerMarkAllRead(undefined);
    updateCache((current) => ({
      ...current,
      notifications: current.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }));
  }, [triggerMarkAllRead, updateCache]);

  const markRead = useCallback(
    async (id: number) => {
      await triggerMarkOneRead(id);
      updateCache((current) => ({
        ...current,
        notifications: current.notifications.map((n) =>
          n.id === id ? { ...n, read: true } : n,
        ),
        unreadCount: Math.max(0, current.unreadCount - 1),
      }));
    },
    [triggerMarkOneRead, updateCache],
  );

  const deleteOne = useCallback(
    async (id: number) => {
      const notification = data?.notifications.find((n) => n.id === id);
      await triggerDelete(id);
      updateCache((current) => ({
        ...current,
        notifications: current.notifications.filter((n) => n.id !== id),
        unreadCount:
          notification && !notification.read
            ? Math.max(0, current.unreadCount - 1)
            : current.unreadCount,
      }));
    },
    [triggerDelete, updateCache, data],
  );

  return {
    notifications: data?.notifications ?? [],
    unreadCount: data?.unreadCount ?? 0,
    loading,
    error,
    refetch,
    markAllRead,
    markRead,
    deleteOne,
  };
}
